import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Calendar, Clock, Tag, Share2, ChevronRight, 
  BookOpen, User, Eye, Twitter, Facebook, Linkedin, Copy, Check,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { API } from '@/lib/api';
import { toast } from 'sonner';
import QuickNav from '@/components/QuickNav';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { Helmet } from 'react-helmet-async';

const BlogPostPage = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentPosts, setRecentPosts] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadPost();
    loadRecentPosts();
  }, [slug]);

  const loadPost = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Error loading post:', error);
      if (error.response?.status === 404) {
        navigate('/blog');
        toast.error('Artigo não encontrado');
      }
    } finally {
      setLoading(false);
    }
  };

  const loadRecentPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/recent?limit=3`);
      setRecentPosts(response.data.filter(p => p.slug !== slug));
    } catch (error) {
      console.error('Error loading recent posts:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { 
      day: '2-digit', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min de leitura`;
  };

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const shareTitle = post?.title || '';

  const handleShare = async (platform) => {
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + shareUrl)}`
    };

    if (platform === 'copy') {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copiado!');
      setTimeout(() => setCopied(false), 2000);
    } else {
      window.open(urls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00FF94] animate-spin" />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{post.title} | Blog Evite Dívidas</title>
        <meta name="description" content={post.meta_description || post.excerpt} />
        <meta name="keywords" content={post.meta_keywords?.join(', ')} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt} />
        <meta property="og:image" content={post.cover_image || 'https://evitedividas.com.br/og-image.svg'} />
        <meta property="og:type" content="article" />
        <meta property="article:published_time" content={post.published_at} />
        <meta property="article:author" content={post.author_name} />
        {post.tags?.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.meta_description || post.excerpt} />
        <meta name="twitter:image" content={post.cover_image || 'https://evitedividas.com.br/og-image.svg'} />
        <link rel="canonical" href={`https://evitedividas.com.br/blog/${post.slug}`} />
      </Helmet>

      <div className="min-h-screen bg-[#050505]">
        {/* Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 glass-heavy">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <Button 
                variant="ghost" 
                className="text-gray-400 hover:text-white ml-12 sm:ml-14" 
                onClick={() => navigate('/blog')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Blog
              </Button>
              <div className="flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-[#00FF94]" />
                <span className="font-bold text-white hidden sm:block">Evite Dívidas</span>
              </div>
              <div className="w-20" />
            </div>
          </div>
        </nav>

        <main className="pt-20 pb-32">
          <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Cover Image */}
            {post.cover_image && (
              <motion.div
                className="aspect-video rounded-2xl overflow-hidden mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <img 
                  src={post.cover_image} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </motion.div>
            )}

            {/* Header */}
            <motion.header
              className="mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.tags.map((tag) => (
                    <Link 
                      key={tag}
                      to={`/blog?tag=${tag}`}
                      className="text-sm px-3 py-1 bg-[#00FF94]/10 text-[#00FF94] rounded-full hover:bg-[#00FF94]/20 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>
              )}

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
                {post.title}
              </h1>

              {/* Subtitle */}
              {post.subtitle && (
                <p className="text-xl text-gray-400 mb-6">
                  {post.subtitle}
                </p>
              )}

              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  <span>{post.author_name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{formatDate(post.published_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{estimateReadTime(post.content)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4" />
                  <span>{post.view_count} visualizações</span>
                </div>
              </div>
            </motion.header>

            {/* Share Buttons */}
            <motion.div
              className="flex items-center gap-3 mb-8 pb-8 border-b border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
            >
              <span className="text-sm text-gray-500">Compartilhar:</span>
              <button
                onClick={() => handleShare('twitter')}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                title="Compartilhar no Twitter"
              >
                <Twitter className="w-4 h-4 text-gray-400" />
              </button>
              <button
                onClick={() => handleShare('facebook')}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                title="Compartilhar no Facebook"
              >
                <Facebook className="w-4 h-4 text-gray-400" />
              </button>
              <button
                onClick={() => handleShare('linkedin')}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                title="Compartilhar no LinkedIn"
              >
                <Linkedin className="w-4 h-4 text-gray-400" />
              </button>
              <button
                onClick={() => handleShare('copy')}
                className="p-2 bg-white/5 hover:bg-white/10 rounded-full transition-colors"
                title="Copiar link"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-[#00FF94]" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-400" />
                )}
              </button>
            </motion.div>

            {/* Content */}
            <motion.div
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white prose-headings:font-bold
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
                prose-a:text-[#00FF94] prose-a:no-underline hover:prose-a:underline
                prose-strong:text-white prose-strong:font-semibold
                prose-ul:text-gray-300 prose-ol:text-gray-300
                prose-li:mb-2
                prose-blockquote:border-[#00FF94] prose-blockquote:bg-[#00FF94]/5 prose-blockquote:rounded-r-lg prose-blockquote:py-1
                prose-code:text-[#00FF94] prose-code:bg-white/5 prose-code:px-1 prose-code:rounded
                prose-pre:bg-[#0A0A0A] prose-pre:border prose-pre:border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <ReactMarkdown remarkPlugins={[remarkBreaks]}>{post.content}</ReactMarkdown>
            </motion.div>

            {/* Share Buttons */}
            <motion.div
              className="mt-10 p-5 bg-[#0A0A0A] border border-white/5 rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.22 }}
            >
              <p className="text-sm text-gray-500 mb-3">Compartilhe este artigo:</p>
              <div className="flex flex-wrap gap-3">
                <a
                  href={`https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + ' - ' + window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#25D366]/10 border border-[#25D366]/20 text-[#25D366] hover:bg-[#25D366]/20 transition-colors text-sm font-medium"
                  data-testid="share-whatsapp"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#1877F2]/10 border border-[#1877F2]/20 text-[#1877F2] hover:bg-[#1877F2]/20 transition-colors text-sm font-medium"
                  data-testid="share-facebook"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Facebook
                </a>
                <a
                  href={`https://pinterest.com/pin/create/button/?url=${encodeURIComponent(window.location.href)}&description=${encodeURIComponent(post.title)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E60023]/10 border border-[#E60023]/20 text-[#E60023] hover:bg-[#E60023]/20 transition-colors text-sm font-medium"
                  data-testid="share-pinterest"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.988C24.007 5.367 18.641 0 12.017 0z"/></svg>
                  Pinterest
                </a>
                <a
                  href="https://www.instagram.com/evite_dividas_oficial"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[#E1306C]/10 border border-[#E1306C]/20 text-[#E1306C] hover:bg-[#E1306C]/20 transition-colors text-sm font-medium"
                  data-testid="share-instagram"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
                  Siga no Instagram
                </a>
              </div>
            </motion.div>

            {/* Author Box */}
            <motion.div
              className="mt-12 p-6 bg-[#0A0A0A] border border-white/10 rounded-2xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
            >
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-[#00FF94]/20 flex items-center justify-center">
                  <User className="w-8 h-8 text-[#00FF94]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Escrito por</p>
                  <p className="text-lg font-bold text-white">{post.author_name}</p>
                  <p className="text-sm text-gray-400">Equipe Evite Dívidas</p>
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              className="mt-12 p-8 bg-gradient-to-r from-[#00FF94]/10 to-emerald-500/10 border border-[#00FF94]/20 rounded-2xl text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="text-2xl font-bold text-white mb-2">
                Gostou do conteúdo?
              </h3>
              <p className="text-gray-400 mb-6">
                Organize suas finanças agora mesmo com nosso organizador gratuito.
              </p>
              <Button
                className="bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold px-8"
                onClick={() => navigate('/expenses')}
              >
                Começar a organizar <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>

            {/* Related Posts */}
            {recentPosts.length > 0 && (
              <motion.div
                className="mt-16"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
              >
                <h2 className="text-2xl font-bold text-white mb-6">Outros artigos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {recentPosts.slice(0, 2).map((relatedPost) => (
                    <Link
                      key={relatedPost.post_id}
                      to={`/blog/${relatedPost.slug}`}
                      className="group bg-[#0A0A0A] border border-white/10 rounded-xl p-4 hover:border-[#00FF94]/30 transition-all"
                    >
                      <h3 className="font-bold text-white group-hover:text-[#00FF94] transition-colors line-clamp-2 mb-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-gray-400 line-clamp-2">
                        {relatedPost.excerpt}
                      </p>
                    </Link>
                  ))}
                </div>
              </motion.div>
            )}
          </article>
        </main>

        <QuickNav />
      </div>
    </>
  );
};

export default BlogPostPage;
